import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageShell from "@/components/proof/PageShell";
import { easing } from "@/lib/animations";

const TIMELINE = [
    {
        year: "Field 001",
        label: "Origin · Live",
        text: "Eleven artifacts. One unit of intent. PROOF begins quietly, in a single Warsaw atelier.",
        to: "/drop/field-001",
    },
    {
        year: "Field 002",
        label: "Slow Expansion · Pre-launch",
        text: "Wool, raw denim, half-canvas tailoring. The line widens — the voice does not.",
        to: "/drop/field-002",
    },
    {
        year: "Field 003",
        label: "On the Horizon",
        text: "Editorial collaborations. New material studies. The same restraint, in a different register.",
        to: null,
    },
];

const TENETS = [
    {
        no: "01",
        title: "Less Noise",
        text: "Silence is a luxury. The product speaks first; everything else, much later, in lowercase.",
    },
    {
        no: "02",
        title: "Honest Construction",
        text: "Heavy cotton. Double-needle seams. Half-canvas tailoring. Materials that age publicly.",
    },
    {
        no: "03",
        title: "Made Slowly · Poland",
        text: "Cut, sewn and finished by people who answer their own phone. No factory white-labelling, no anonymous supply chain.",
    },
    {
        no: "04",
        title: "Built to Outlast Trends",
        text: "Hype is a tax on uncertainty. We are not interested in it.",
    },
];

export default function About() {
    return (
        <PageShell
            sectionNumber="06"
            eyebrow="Atelier · Warsaw"
            title="Manifest"
            crumb="About"
            data-testid="page-about"
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(70px, 9vw, 140px)",
                }}
            >
                {/* INTRO STATEMENT */}
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.9, ease: easing.primary }}
                    style={{
                        margin: 0,
                        fontFamily: "'EB Garamond', serif",
                        fontStyle: "italic",
                        fontSize: "clamp(22px, 2.4vw, 36px)",
                        lineHeight: 1.4,
                        color: "rgba(255,255,255,0.9)",
                        maxWidth: "32ch",
                    }}
                    data-testid="about-intro"
                >
                    The world keeps talking. Most of it means nothing. Your
                    instinct knew first. We just design for the second voice.
                </motion.p>

                {/* TENETS GRID */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "clamp(22px, 2.5vw, 40px)",
                        borderTop: "1px solid rgba(255,255,255,0.14)",
                        paddingTop: "clamp(36px, 4vw, 60px)",
                    }}
                    data-testid="about-tenets"
                >
                    {TENETS.map((t, i) => (
                        <motion.div
                            key={t.no}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.08 * i,
                                ease: easing.primary,
                            }}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                            }}
                            data-testid={`about-tenet-${t.no}`}
                        >
                            <span
                                style={{
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.32em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.45)",
                                }}
                            >
                                [{t.no}]
                            </span>
                            <h3
                                style={{
                                    margin: 0,
                                    fontFamily:
                                        "'Space Grotesk', Manrope, sans-serif",
                                    fontWeight: 700,
                                    fontSize: "clamp(22px, 1.9vw, 30px)",
                                    letterSpacing: "-0.02em",
                                    textTransform: "uppercase",
                                    color: "#fff",
                                }}
                            >
                                {t.title}
                            </h3>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily:
                                        "'Space Grotesk', Manrope, sans-serif",
                                    fontSize: 14,
                                    lineHeight: 1.65,
                                    color: "rgba(255,255,255,0.7)",
                                    maxWidth: "34ch",
                                }}
                            >
                                {t.text}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* TIMELINE */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 30,
                    }}
                    data-testid="about-timeline"
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "baseline",
                            paddingBottom: 14,
                            borderBottom: "1px solid rgba(255,255,255,0.14)",
                        }}
                    >
                        <h2
                            style={{
                                margin: 0,
                                fontFamily:
                                    "'Space Grotesk', Manrope, sans-serif",
                                fontWeight: 700,
                                fontSize: "clamp(26px, 3vw, 42px)",
                                letterSpacing: "-0.025em",
                                textTransform: "uppercase",
                            }}
                        >
                            Field Notes
                        </h2>
                        <span
                            style={{
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: 10,
                                letterSpacing: "0.28em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.5)",
                            }}
                        >
                            Logbook
                        </span>
                    </div>
                    {TIMELINE.map((step, i) => (
                        <motion.div
                            key={step.year}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{
                                duration: 0.8,
                                delay: 0.06 * i,
                                ease: easing.primary,
                            }}
                            style={{
                                paddingBottom: 26,
                                borderBottom:
                                    "1px solid rgba(255,255,255,0.08)",
                            }}
                            className="proof-grid-timeline"
                            data-testid={`about-timeline-${i}`}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    fontFamily: "JetBrains Mono, monospace",
                                    fontSize: 11,
                                    letterSpacing: "0.24em",
                                    textTransform: "uppercase",
                                }}
                            >
                                {step.to ? (
                                    <Link
                                        to={step.to}
                                        data-testid={`about-timeline-link-${i}`}
                                        style={{
                                            color: "#fff",
                                            textDecoration: "none",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                        }}
                                    >
                                        {step.year}
                                        <span aria-hidden="true">→</span>
                                    </Link>
                                ) : (
                                    <span style={{ color: "#fff" }}>
                                        {step.year}
                                    </span>
                                )}
                                <span style={{ color: "rgba(255,255,255,0.5)" }}>
                                    {step.label}
                                </span>
                            </div>
                            <p
                                style={{
                                    margin: 0,
                                    fontFamily:
                                        "'EB Garamond', serif",
                                    fontStyle: "italic",
                                    fontSize: 18,
                                    lineHeight: 1.55,
                                    color: "rgba(255,255,255,0.85)",
                                    maxWidth: "60ch",
                                }}
                            >
                                {step.text}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* CTA */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 18,
                    }}
                >
                    <p
                        style={{
                            margin: 0,
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontWeight: 500,
                            fontSize: "clamp(22px, 2.4vw, 36px)",
                            letterSpacing: "-0.02em",
                            color: "#fff",
                            maxWidth: "26ch",
                            lineHeight: 1.18,
                        }}
                    >
                        The product speaks first.
                    </p>
                    <Link
                        to="/shop"
                        data-testid="about-shop-cta"
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 12,
                            color: "#fff",
                            textDecoration: "none",
                            border: "1px solid rgba(255,255,255,0.45)",
                            padding: "16px 28px",
                            fontFamily: "JetBrains Mono, monospace",
                            fontSize: 11,
                            letterSpacing: "0.28em",
                            textTransform: "uppercase",
                        }}
                    >
                        Enter Lookbook
                        <svg width="22" height="9" viewBox="0 0 22 9" fill="none">
                            <line x1="0" y1="4.5" x2="20" y2="4.5" stroke="#fff" />
                            <path d="M16 1L20 4.5L16 8" stroke="#fff" fill="none" />
                        </svg>
                    </Link>
                </div>
            </div>
        </PageShell>
    );
}
